import { useEffect, useRef, useState } from 'react';

interface WakeLockSentinelLike {
  released: boolean;
  release: () => Promise<void>;
  addEventListener: (type: string, listener: () => void) => void;
}

/**
 * Holds a screen wake lock while `active`, so a professor's phone does not
 * sleep mid-lecture.
 *
 * The OS drops the lock every time the tab is hidden and never restores it on
 * its own, so we re-request on visibilitychange. Some browsers also only grant
 * one after a user gesture, hence the pointerdown retry.
 *
 * Returns the *real* state rather than an assumption: `held` is false on
 * browsers without the API (any iOS before 16.4) so the UI can stay honest
 * about whether the screen will actually stay on.
 */
export function useWakeLock(active: boolean): { supported: boolean; held: boolean } {
  const [held, setHeld] = useState(false);
  const sentinelRef = useRef<WakeLockSentinelLike | null>(null);
  const supported = typeof navigator !== 'undefined' && 'wakeLock' in navigator;

  useEffect(() => {
    if (!supported || !active) return;
    let cancelled = false;

    const acquire = async () => {
      if (cancelled || document.visibilityState !== 'visible') return;
      if (sentinelRef.current && !sentinelRef.current.released) return;
      try {
        const sentinel: WakeLockSentinelLike = await (
          navigator as unknown as { wakeLock: { request: (t: string) => Promise<WakeLockSentinelLike> } }
        ).wakeLock.request('screen');
        if (cancelled) {
          void sentinel.release().catch(() => {});
          return;
        }
        sentinelRef.current = sentinel;
        setHeld(true);
        sentinel.addEventListener('release', () => setHeld(false));
      } catch {
        // Denied (battery saver, no gesture yet, tab hidden). The listeners
        // below give it another chance rather than failing for the session.
        setHeld(false);
      }
    };

    void acquire();

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void acquire();
    };
    const onPointerDown = () => {
      void acquire();
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pointerdown', onPointerDown);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pointerdown', onPointerDown);
      const sentinel = sentinelRef.current;
      sentinelRef.current = null;
      setHeld(false);
      if (sentinel && !sentinel.released) void sentinel.release().catch(() => {});
    };
  }, [active, supported]);

  return { supported, held };
}
